import { applyReaderFont, DEFAULT_READER_FONT, readerFont, type ReaderFont } from './typography.js';

import { initDrawers } from './drawers';

interface DisplayPrefs {
  fs: number;
  lh: number;
  cw: number;
  font: ReaderFont;
  ipaOriginals: boolean;
}

const DEFAULTS: DisplayPrefs = { fs: 18, lh: 1.95, cw: 620, font: DEFAULT_READER_FONT, ipaOriginals: false };
const PREFS_KEY = 'margin.display';
const THEME_KEY = 'margin.theme';

function loadPrefs(): DisplayPrefs {
  try {
    const saved = JSON.parse(localStorage.getItem(PREFS_KEY) ?? '{}') as Partial<DisplayPrefs> | null;
    const number = (value: unknown, min: number, max: number, fallback: number) =>
      typeof value === 'number' && Number.isFinite(value) ? Math.max(min, Math.min(max, value)) : fallback;
    return {
      fs: number(saved?.fs, 15, 21, DEFAULTS.fs),
      lh: number(saved?.lh, 1.7, 2.4, DEFAULTS.lh),
      cw: number(saved?.cw, 520, 760, DEFAULTS.cw),
      font: readerFont(saved?.font),
      ipaOriginals: saved?.ipaOriginals === true,
    };
  } catch {
    return { ...DEFAULTS };
  }
}

function applyPrefs(prefs: DisplayPrefs) {
  const root = document.documentElement;
  root.style.setProperty('--reader-fs', `${prefs.fs}px`);
  root.style.setProperty('--reader-lh', String(prefs.lh));
  root.style.setProperty('--column-w', `${prefs.cw}px`);
  const changed = root.dataset.readerFont !== prefs.font || root.dataset.ipaOriginals !== String(prefs.ipaOriginals);
  root.dataset.ipaOriginals = String(prefs.ipaOriginals);
  if (prefs.ipaOriginals) root.style.setProperty('--font-ui', '"Margin IPAexGothic", "Segoe UI", "Microsoft YaHei", sans-serif');
  else root.style.removeProperty('--font-ui');
  if (changed) applyReaderFont(prefs.font, prefs.ipaOriginals);
}

function applyTheme(theme: 'day' | 'night') {
  document.documentElement.dataset.theme = theme;
  const button = document.getElementById('themeToggle')!;
  button.innerHTML = theme === 'night'
    ? '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="3.3"/><path d="M10 1.8v1.5m0 13.4v1.5M1.8 10h1.5m13.4 0h1.5M4.2 4.2l1.1 1.1m9.4 9.4 1.1 1.1m0-11.6-1.1 1.1m-9.4 9.4-1.1 1.1"/></svg>'
    : '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M16.5 11.4A6.8 6.8 0 0 1 8.6 3.5a6.8 6.8 0 1 0 7.9 7.9Z"/></svg>';
  button.setAttribute('aria-label', theme === 'night' ? '切换日间模式' : '切换夜览模式');
  button.title = theme === 'night' ? '日间模式' : '夜览模式';
}

export function initSettings() {
  const prefs = loadPrefs();
  applyPrefs(prefs);
  applyTheme(localStorage.getItem(THEME_KEY) === 'night' ? 'night' : 'day');

  const panel = document.getElementById('displayPanel')!;
  const fs = document.getElementById('fsRange') as HTMLInputElement;
  const lh = document.getElementById('lhRange') as HTMLInputElement;
  const cw = document.getElementById('cwRange') as HTMLInputElement;
  const font = document.getElementById('readerFont') as HTMLSelectElement;
  const ipaOriginals = document.getElementById('ipaOriginals') as HTMLInputElement;
  const fsVal = document.getElementById('fsVal')!;
  const lhVal = document.getElementById('lhVal')!;
  const cwVal = document.getElementById('cwVal')!;

  const sync = () => {
    fs.value = String(prefs.fs);
    lh.value = String(prefs.lh);
    cw.value = String(prefs.cw);
    font.value = prefs.font;
    ipaOriginals.checked = prefs.ipaOriginals;
    fsVal.textContent = `${prefs.fs}px`;
    lhVal.textContent = prefs.lh.toFixed(2);
    cwVal.textContent = `${prefs.cw}px`;
    applyPrefs(prefs);
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  };

  fs.addEventListener('input', () => { prefs.fs = Number(fs.value); sync(); });
  lh.addEventListener('input', () => { prefs.lh = Number(lh.value); sync(); });
  cw.addEventListener('input', () => { prefs.cw = Number(cw.value); sync(); });
  font.addEventListener('change', () => { prefs.font = readerFont(font.value); sync(); });
  ipaOriginals.addEventListener('change', () => { prefs.ipaOriginals = ipaOriginals.checked; sync(); });
  sync();

  const displayBtn = document.getElementById('displayBtn')!;
  displayBtn.addEventListener('click', () => {
    panel.hidden = !panel.hidden;
    displayBtn.classList.toggle('on', !panel.hidden);
  });
  document.addEventListener('mousedown', (e) => {
    if (!panel.hidden && !panel.contains(e.target as Node) && !displayBtn.contains(e.target as Node)) {
      panel.hidden = true;
      displayBtn.classList.remove('on');
    }
  });

  document.getElementById('themeToggle')!.addEventListener('click', () => {
    const night = document.documentElement.dataset.theme !== 'night';
    applyTheme(night ? 'night' : 'day');
    localStorage.setItem(THEME_KEY, night ? 'night' : 'day');
  });

  const app = document.getElementById('app')!;
  const focusBtn = document.getElementById('focusBtn')!;
  const setFocus = (on: boolean) => {
    app.classList.toggle('focus', on);
    focusBtn.classList.toggle('on', on);
  };
  focusBtn.addEventListener('click', () => setFocus(!app.classList.contains('focus')));
  initDrawers(() => setFocus(false));
}
